import CatalogItem, {
  CatalogSuggestion,
  PriceReference,
} from "../models/catalog-item";
import { FAILED } from "../utils/constants/tags";
import { deleteItem, getItem, getList, postItem, updateItem } from "./generics";
import { getAbsoluteUrl } from "./urls";
import { fetchAuthenticated } from "./utils";

export const getCatalogItemList = (
  taxPayerId: number,
  search: string,
  limit: number,
  offset: number,
  includeInactive: boolean = false
) => {
  const additionalParams = new Map<string, string>();
  additionalParams.set("tax_payer_id", taxPayerId.toString());

  if (search !== "") {
    additionalParams.set("search", search);
  }

  if (includeInactive) {
    additionalParams.set("include_inactive", "true");
  }

  return getList<CatalogItem>("/catalog_items/", limit, offset, additionalParams);
};

export const createCatalogItem = (data: CatalogItem) => {
  return postItem<CatalogItem>("/catalog_items/create/", data);
};

export const updateCatalogItem = (id: number, data: CatalogItem) => {
  return updateItem<CatalogItem>(`/catalog_items/${id}/`, data);
};

/** Deactivates: the documents that used the entry still point at it. */
export const deactivateCatalogItem = (id: number, taxPayerId: number) => {
  return deleteItem(`/catalog_items/${id}/`, { taxPayerId });
};

export const createCatalogItemsBulk = (
  taxPayerId: number,
  items: CatalogItem[]
) => {
  return postItem<{ created: number; skipped: number; linked: number }>(
    "/catalog_items/bulk_create/",
    { taxPayerId, items } as any
  );
};

/**
 * What this entry was last charged at, for the grey line under the price.
 *
 * This client first: a price given to one customer is not the price given to
 * the rest, and showing the general one pushes towards the wrong number.
 */
export const getCatalogItemPrices = (
  id: number,
  taxPayerId: number,
  clientRuc?: string
) => {
  const additionalParams = new Map<string, string | undefined>();
  additionalParams.set("tax_payer_id", taxPayerId.toString());
  additionalParams.set("client_ruc", clientRuc);

  return getItem<{ prices: number[]; forThisClient: boolean }>(
    `/catalog_items/${id}/prices/`,
    additionalParams
  );
};

/** Whether a written price is an order of magnitude off what is usual. */
export const getPriceReference = (
  taxPayerId: number,
  description: string,
  code: string | undefined,
  unitAmount: number
) => {
  const additionalParams = new Map<string, string | undefined>();
  additionalParams.set("tax_payer_id", taxPayerId.toString());
  additionalParams.set("description", description);
  additionalParams.set("code", code);
  additionalParams.set("unit_amount", unitAmount.toString());

  return getItem<PriceReference>(
    "/catalog_items/price_reference/",
    additionalParams
  );
};

/**
 * Of the descriptions just invoiced, which are worth offering to add.
 *
 * Written by hand rather than through `getItem` because the description
 * parameter repeats, and the map the generic helpers take keeps one value per
 * key.
 */
export const getUnlistedDescriptions = async (
  taxPayerId: number,
  descriptions: string[]
) => {
  const params = new URLSearchParams();
  params.set("tax_payer_id", taxPayerId.toString());

  for (const description of descriptions) {
    params.append("description", description);
  }

  const fullUrl =
    getAbsoluteUrl("/catalog_items/unlisted/") + "?" + params.toString();

  try {
    const response = await fetchAuthenticated(fullUrl, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "GET",
      credentials: "include",
    });

    return (await response.json()) as {
      status: string;
      detail?: string;
      data?: { suggestions: CatalogSuggestion[] };
    };
  } catch {
    return { status: FAILED, detail: "Server not Found" };
  }
};
